import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { BadgeAppearance } from '../../types/badge-appearance';
import { BadgeVariant } from '../../types/badge-variant';

@Component({
  selector: 'app-badge',
  imports: [],
  templateUrl: './badge.component.html',
  styleUrl: './badge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BadgeComponent {
  public readonly appearance = input<BadgeAppearance>('solid');
  public readonly uppercase = input<boolean>(true);
  public readonly variant = input<BadgeVariant>('neutral');

  protected readonly badgeCssClass = computed(() => {
    const classes = [
      'inline-flex',
      'rounded-full',
      'px-3',
      'py-1',
      'text-xs',
      'font-semibold',
      'tracking-[0.08em]',
    ];

    if (this.uppercase()) {
      classes.push('uppercase');
    }

    classes.push(...this.resolveVariantClasses());

    return classes.join(' ');
  });

  private resolveVariantClasses(): string[] {
    const appearance = this.appearance();
    const variant = this.variant();

    switch (`${variant}:${appearance}`) {
      case 'primary:solid':
        return ['bg-[#dc6b95]', 'text-white'];
      case 'primary:soft':
        return ['bg-rose-50', 'text-rose-600'];
      case 'primary:outline':
        return ['border', 'border-[#dc6b95]', 'text-[#dc6b95]'];

      case 'success:solid':
        return ['bg-emerald-600', 'text-white'];
      case 'success:soft':
        return ['bg-emerald-50', 'text-emerald-700'];
      case 'success:outline':
        return ['border', 'border-emerald-300', 'text-emerald-700'];

      case 'info:solid':
        return ['bg-sky-600', 'text-white'];
      case 'info:soft':
        return ['bg-sky-50', 'text-sky-700'];
      case 'info:outline':
        return ['border', 'border-sky-300', 'text-sky-700'];

      case 'warning:solid':
        return ['bg-amber-500', 'text-white'];
      case 'warning:soft':
        return ['bg-amber-50', 'text-amber-700'];
      case 'warning:outline':
        return ['border', 'border-amber-300', 'text-amber-700'];

      case 'danger:solid':
        return ['bg-red-600', 'text-white'];
      case 'danger:soft':
        return ['bg-red-50', 'text-red-700'];
      case 'danger:outline':
        return ['border', 'border-red-300', 'text-red-700'];

      case 'neutral:solid':
        return ['bg-gray-500', 'text-white'];
      case 'neutral:soft':
        return ['bg-gray-100', 'text-gray-700'];
      case 'neutral:outline':
      default:
        return ['border', 'border-gray-300', 'text-gray-500'];
    }
  }
}
